// Decorative collections — the "composed-string" / aesthetic tier.
//
// Unlike the single-symbol catalogue (src/data/symbols.ts), each entry here is a
// ready-made MULTI-character combo that is copied as a whole string: text
// borders, dividers, loading bars, sparkles, hanging stars, waves, music-player
// bars and aesthetic flourishes. Hand-authored and curated; no UCD pipeline.
// scripts/validate.mjs (section 21) enforces that every collection has combos,
// combos are unique within a collection, descriptions/intros are unique, and no
// placeholder tokens (text, name, [text], @user, :shortcode:) leak into a combo.

export interface DecorativeCollection {
  slug: string;
  name: string;
  /** A representative combo shown as the page/preview hero. */
  hero: string;
  tagline: string;
  intro: string[];
  /** The copyable combos. Borders use "\n" between the top and bottom line. */
  combos: string[];
}

export const decorativeCollections: DecorativeCollection[] = [
  {
    slug: "borders",
    name: "Text Borders",
    hero: "╔══════╗\n╚══════╝",
    tagline: "Decorative text frames and box borders to wrap a name, title or quote.",
    intro: [
      "Text borders are two-line frames built from box-drawing characters — a top edge and a matching bottom edge that you place above and below your text. Drop your words on the middle line and the frame turns a plain message into a tidy little plaque.",
      "Each border below copies both lines at once. Paste them around a username, a header or a short note in a bio, document or chat where a real box element isn't available.",
    ],
    combos: [
      "╭┈┈┈┈╮\n╰┈┈┈┈╯",
      "╭─────╮\n╰─────╯",
      "┌──────┐\n└──────┘",
      "╒══════╕\n╘══════╛",
      "╔══════╗\n╚══════╝",
      "╔══•●•══╗\n╚══•●•══╝",
      "┌── •✧• ──┐\n└── •✧• ──┘",
      "╔═.✾. ════╗\n╚════.✾. ═╝",
      "┌── ⋆⋅☆⋅⋆ ──┐\n└── ⋆⋅☆⋅⋆ ──┘",
      "╭──〔❨✧✧❩〕──╮\n╰──〔❨✧✧❩〕──╯",
      "┌─── ∘°❉°∘ ───┐\n└─── °∘❉∘° ───┘",
      "╔══《✧》══╗\n╚══《✧》══╝",
      "┏━✦❘༻༺❘✦━━┓\n┗━✦❘༻༺❘✦━━┛",
      "┍━☽【❖】☾━┑\n┕━☽【❖】☾━┙",
      "╔══ஓ๑♡๑ஓ══╗\n╚══ஓ๑♡๑ஓ══╝",
      "┏━━━━━༻❁༺━━━━━┓\n┗━━━━━༻❁༺━━━━━┛",
      "╭━━━━━∙⋆⋅⋆∙━━━━━╮\n╰━━━━━∙⋆⋅⋆∙━━━━━╯",
      "╔══❖═══════❖══╗\n╚══❖═══════❖══╝",
      "╔═══✿════════✿═══╗\n╚═══✿════════✿═══╝",
      "╔═══════☆♡☆═══════╗\n╚═══════☆♡☆═══════╝",
      "╔══ ❀•°❀°•❀ ══╗\n╚══ ❀•°❀°•❀ ══╝",
      "┏━•❃°•°❀°•°❃•━┓\n┗━•❃°•°❀°•°❃•━┛",
      "┌─────━┿──┿━─────┐\n└─────━┿──┿━─────┘",
      "╔════ ✿ ❀ ღღ ✿ ❀ ════╗\n╚════ ❀ ✿ ღღ ❀ ✿ ════╝",
      "┏━━━━━•❅•°•❈•°•❅•━━━━━┓\n┗━━━━━•❅•°•❈•°•❅•━━━━━┛",
      "┌──═━┈━═──┐\n└──═━┈━═──┘",
      "╔══ ≪ °❈° ≫ ══╗\n╚══ ≪ °❈° ≫ ══╝",
      "╭ ─┉─ • ─┉─ ╮\n╰ ─┉─ • ─┉─ ╯",
      "╔══════✮❁•°♛°•❁✮══════╗\n╚══════✮❁•°❀°•❁✮══════╝",
      "┏━━━━━•°•°•❈•°•°•━━━━━┓\n┗━━━━━•°•°•❈•°•°•━━━━━┛",
      "╔══✿══════✿══╗\n╚══✿══════✿══╝",
      "╔═══ ✧ ═══╗\n╚═══ ✧ ═══╝",
      "┌─❀─────❀─┐\n└─❀─────❀─┘",
      "╭⋆⋅☆⋅⋆⋆⋅☆⋅⋆╮\n╰⋆⋅☆⋅⋆⋆⋅☆⋅⋆╯",
    ],
  },
  {
    slug: "dividers",
    name: "Text Dividers",
    hero: "─── ⋆⋅☆⋅⋆ ───",
    tagline: "Single-line dividers and section breaks for bios, posts and profiles.",
    intro: [
      "Dividers are one-line flourishes that separate sections of text — a decorative substitute for a horizontal rule. They sit nicely between paragraphs, profile sections or song lyrics where you want a soft visual break.",
      "Tap any divider to copy it, then paste it on its own line. Mix and match a few down a page for a consistent, hand-made look.",
    ],
    combos: [
      "──────────",
      "︶︶︶︶︶︶︶︶︶",
      "⌣ ‿ ‿ ‿ ‿ ‿ ‿ ⌣",
      "─── ⋆⋅☆⋅⋆ ───",
      "∘₊✧──────✧₊∘",
      "•┈••✦ ❤ ✦••┈•",
      "───※ ·❆· ※───",
      "•❅───✧❅✦❅✧───❅•",
      "-ˏˋ✄┈┈┈┈",
      "❀° ── ⋅ ── ✩ ── ⋅ ──",
      "────୨ৎ────",
      "─── ✯ ━━━━ ✿ ━━━━ ✯ ───",
      "·˚ ༘₊·꒰➳: ̗̀➛",
      "꒷꒦︶꒷꒦︶꒷꒦",
      "✦•┈๑⋅⋯ ⋯⋅๑┈•✦",
      "╴╴╴╴╴╴ ⚘ ╴╴╴╴╴╴",
      "⋆˙⟡ ─────── ⟡˙⋆",
      "꒰⊹ ───── ⊹꒱",
      "•°. ─────── .°•",
      "❥─────────────",
      "│ ✦ │ ─────────",
      "︶︶︶︶ ✿ ︶︶︶︶",
      "•❣•୨୧┈┈┈୨୧•❣•",
      "჻჻჻჻჻჻჻჻჻჻჻჻",
      "❀° ┄───╮ ╰───┄ °❀",
      "─━━━━━━⊱✫⊰━━━━━━─",
      "═══ ✧ ═══",
      "⊰ ━━━━━━ ⊱",
      "•°• ─────── •°•",
      "⋆ ━━━━━ ⋆ ━━━━━ ⋆",
      "❍ ─────── ❍",
      "˚ ༘ ⋆｡˚ ─── ˚｡⋆ ༘ ˚",
    ],
  },
  {
    slug: "loading-bars",
    name: "Loading Bars",
    hero: "███████▒▒▒ 70%",
    tagline: "Progress and loading-bar combos for status messages and bios.",
    intro: [
      "Loading bars are little progress meters built from block characters. They read like a download or status bar and are a playful way to show \"how far along\" something is — a mood, a project, or just for fun in a bio.",
      "Copy a bar and drop it into a profile or message. Lower the fill or swap the percentage to match whatever you're tracking.",
    ],
    combos: [
      "█▒▒▒▒▒▒▒▒▒ 10%",
      "██▒▒▒▒▒▒▒▒ 20%",
      "███▒▒▒▒▒▒▒ 30%",
      "████▒▒▒▒▒▒ 40%",
      "█████▒▒▒▒▒ 50%",
      "██████▒▒▒▒ 60%",
      "███████▒▒▒ 70%",
      "████████▒▒ 80%",
      "█████████▒ 90%",
      "██████████ 100%",
      "[■□□□□□□□□□] 10%",
      "[■■■□□□□□□□] 30%",
      "[■■■■■□□□□□] 50%",
      "[■■■■■■■□□□] 70%",
      "[■■■■■■■■■□] 90%",
      "[■■■■■■■■■■] 100%",
      "▰▰▰▱▱▱▱▱▱▱ 30%",
      "▰▰▰▰▰▰▱▱▱▱ 60%",
      "▱▰▰▰▰▰▰▰▰▰ 100%",
      "█░░░░░░░░░ 10%",
      "█████░░░░░ 50%",
      "█████████░ 90%",
      "▒▒▒▒▒▒▒▒▒▒ 100% ᴄᴏᴍᴘʟᴇᴛᴇ",
      "██████████ ✓ ᴅᴏɴᴇ",
      "◖■■■■■□□□□□◗ 50%",
      "⟦████████░░⟧ 80%",
      "▰▰▰▰▰▰▰▰▰▰ ✓",
    ],
  },
  {
    slug: "sparkles",
    name: "Sparkle Symbols",
    hero: "✧･ﾟ: *✧･ﾟ:*",
    tagline: "Sparkle and twinkle combos to add a little shine to text.",
    intro: [
      "Sparkles are clusters of stars, dots and twinkles that scatter a bit of shine around your words. They're a softer, busier cousin of a single star — great for emphasising a name or a special line.",
      "Tap any sparkle to copy it. Place one on each side of your text, or tuck a small one at the end of a sentence for a gentle finish.",
    ],
    combos: [
      "✧･ﾟ: *✧･ﾟ:*",
      "*:･ﾟ✧*:･ﾟ✧",
      "⋇⋆✦⋆⋇",
      "⭒❃.✮:▹",
      "‧͙⁺˚*･༓☾",
      ".｡.:*✧ ✧*:.｡.",
      "✧༝┉˚*❋❤❋*˚┉༝✧",
      "✺✳ ┅ ⑅ ┅ ✳✺",
      "✧༺♥༻∞",
      "*:..｡o○☆",
      ".⋆｡⋆☂˚｡⋆｡˚☽˚｡⋆.",
      "｡･:*:･ﾟ★,｡･:*:･ﾟ☆",
      "☆.｡.:* ☆○o",
      "【☆】★【☆】",
      ".・゜゜・ ・゜゜・．",
      "°。°。°。°。°。°。",
      "✲꘏ ꘏ ꘏ ꘏✲",
      "✼ ҉ ✼ ҉ ✼",
      "ﾟ+..｡*ﾟ+",
      "⋆ ˚｡⋆୨୧˚",
      "⋆｡°✩ ✧",
      "˚ ✦ ⭒ ˚ ✦ ⭒ ˚",
      "✦ ✧ ✦ ✧ ✦",
      "˚ ₊ ‧ ꒰ ✧ ꒱ ‧ ₊ ˚",
      "⊹ ˚ ✧ ˚ ⊹",
      "꒷ ✧ ꒦ ✧ ꒷",
      "⋆ ˚ ✦ ˚ ⋆ ˚ ✦ ˚ ⋆",
      "₊ ⊹ ✧ ⊹ ₊",
    ],
  },
  {
    slug: "hanging-stars",
    name: "Hanging Stars",
    hero: "˚*•̩̩͙✩•̩̩͙*˚＊",
    tagline: "Delicate hanging-star and dangling-charm combos for a soft aesthetic.",
    intro: [
      "Hanging stars use tiny combining marks to give stars and hearts little \"threads,\" so they read like charms dangling from a line. They lend a gentle, handcrafted feel to a bio or caption.",
      "These rely on stacked diacritics, so a few platforms render them more plainly than others. Tap one to copy and try it where you'll use it.",
    ],
    combos: [
      "＊*•̩̩͙✩•̩̩͙*˚",
      "˚*•̩̩͙✩•̩̩͙*˚＊",
      "·͙*̩̩͙˚̩̥̩̥*̩̩̥͙ ✩ *̩̩̥͙˚̩̥̩̥*̩̩͙‧͙",
      "-•̩̩͙-ˏˋ⋆ ⋆ˊˎ-•̩̩͙-",
      "·̩̩̥͙＊*•̩̩͙✩•̩̩͙*˚ ˚*•̩̩͙✩•̩̩͙*˚＊·̩̩̥͙",
      "-ˏˋ♥̩͙♥̩̩̥͙♥̩̥̩ ⑅⑅ ♥̩̥̩♥̩̩̥͙♥̩͙ˊˎ",
      "༝̩̩̥͙ ༓༝̩̩̥͙ ⊹ ⊹༝̩̩̥͙ ༓༝̩̩̥͙",
      "⁎̩͙ ⁑̩͙̩͙ ⁂̩̩͙͙ ⁂̩̩͙͙ ⁑̩͙̩͙ ⁎̩͙",
      "﹥ˏˋ♡̩͙♡̩̩̥͙♡̩̥̩ ♡̩̥̩♡̩̩̥͙♡̩͙ˊˎ﹤",
      "┉ˏ͛ ༝̩̩̥͙ ⑅͚˚ ҉ ⑅͚˚༝̩̩̥͙ˎ┉",
      "ˏ⸉ˋ‿̩͙‿̩̩̥͙̽‿̩͙ ‿̩̥̩‿̩̩̥͙̽‿̩͙ˊ⸊ˎ",
    ],
  },
  {
    slug: "waves",
    name: "Wave Symbols",
    hero: ".·:*¨¨*:·.",
    tagline: "Flowing wave and swirl combos for soft, rippling text accents.",
    intro: [
      "Wave combos string together curves, dots and dashes into a flowing line that ripples across the page. They make a softer divider than a straight rule and pair nicely with music, ocean or dreamy themes.",
      "Copy a wave and drop it on its own line, or frame a short phrase between two matching halves.",
    ],
    combos: [
      ".·:*¨¨*:·.",
      "•*´¨`*•.¸¸.•*´¨`*•.¸¸.•",
      "‿︵•‿︵•‿︵•‿︵",
      "¸¸♫·¯·♪¸¸♩·¯·♬¸¸",
      "☆.。.:*・°☆.。.:*・°☆",
      "✯¸.•´*¨`*•✿ ✿•*`¨*`•.¸✯",
      "¸,ø¤º°`°º¤ø,¸¸,ø¤º°°º¤ø,¸",
      "°°°·.°·..·°¯°·._.··._.·°¯°·.·°",
      ".•°°•..•°°•..•°°•.",
      "∴.·:*¨¨*:·.",
      "❧.·:*¨ ¨*:·.∴",
      "☙.·:*¨ ¨*:·.♡",
      "⠂⠁⠈⠂⠄⠄⠂⠁⠁⠂⠄⠄⠂⠁⠁⠂",
      "-⎽__⎽-⎻⎺⎺⎻-⎽__⎽--⎻⎺⎺⎻-",
      "⋅.˳˳.⋅ॱ˙˙ॱ⋅.˳˳.⋅",
      "♪♫•*¨*•.¸¸❤¸¸.•*¨*•♫♪",
      "彡彡◦༄◦°˚°◦.¸¸彡彡",
      "‿‿‿‿‿‿‿‿",
      "︵‿︵‿︵‿︵",
      "～～～～～～～",
      ".·°°·..·°°·..·°°·.",
      "⌒⌒⌒⌒⌒⌒⌒",
      ".·°•°·..·°•°·.",
      "≈≈≈≈≈≈≈≈",
      "〜〜〜〜〜",
    ],
  },
  {
    slug: "music-players",
    name: "Music Player Bars",
    hero: "↻ ◁ II ▷ ↺",
    tagline: "Now-playing controls, seek bars and volume meters built from symbols.",
    intro: [
      "Music-player combos mimic the controls of a media player — play, pause, skip, a seek bar and a volume meter. They're a fun way to set a \"now playing\" mood in a bio or status.",
      "Copy a control row or a bar and paste it under a song title or lyric. Swap the filled blocks to nudge the progress or volume.",
    ],
    combos: [
      "► ▌▌ ■",
      "↻ ◁ II ▷ ↺",
      "◁◁ ▐ ▌ ▷▷",
      "|◁ II ▷|",
      "↻ ◁ || ▷",
      "◃◃ ⅠⅠ ▹▹",
      "⇄ ◁ II ▷ ↻",
      "ᴠᴏʟᴜᴍᴇ ▮▮▮▮▮▮▯▯▯",
      "■■■■■□□□",
      "▁ ▂ ▃ ▄ ▅ ▆ █",
      "0:00 ───|────── 3:47",
      "1:35 ───●───── 3:47",
      "↺ ᴿᴱᴾᴱᴬᵀ ‖ ᴾᴬᵁˢᴱ ≫ ᴺᴱˣᵀ",
      "▄ █ ▄ █ ▄ ▄ █ ▄ █ ▄ █",
      "◁ II ▷  ⠀ ──●───── ",
      "▶ ━━●─────── ⟲",
    ],
  },
  {
    slug: "hearts",
    name: "Heart Combos",
    hero: "˚ʚ♡ɞ˚",
    tagline: "Love-themed heart combos and flourishes for sweet, soft text.",
    intro: [
      "Heart combos cluster hearts with dots, sparkles and curls into little tokens of affection. They're perfect for love notes, couple bios and anything that wants a softer, warmer feel.",
      "Tap a combo to copy it, then frame a name or wrap a short message between two matching halves.",
    ],
    combos: [
      "♡⸝⸝",
      "｡₊°༺❤︎༻°₊｡",
      "♥••*´¨`*•.☆•",
      "•❀.•❤•.¸✿¸.•❤•.❀•",
      "↘❀↗↘❤↗↘❤↗↘❀↗",
      "｡･:*:･ﾟ❤,｡･:*:･ﾟ❤",
      "⟡ ♡ ⟡",
      "꒰ ♡ ꒱",
      "𓆩♡𓆪",
      "⋆｡°✩ ♡ ✩°｡⋆",
      "❣ ⋆ ❣",
      "♡ ─── ♡ ─── ♡",
      "˚ʚ♡ɞ˚",
      "⊹ ♡ ⊹ ♡ ⊹",
      "♥̩͙ ♥̩̩̥͙ ♥̩̥̩",
      "·͜· ♡",
      "ღ♡ღ♡ღ♡ღ♡ღ",
      "˚｡❤｡˚ ⋆ ˚｡❤｡˚",
      "♡｡˚♡｡˚♡｡˚♡｡˚♡",
    ],
  },
  {
    slug: "dots",
    name: "Dot Dividers",
    hero: "●・○・●・○・●",
    tagline: "Dot-and-bead dividers and beaded lines for tidy section breaks.",
    intro: [
      "Dot dividers thread small circles, beads and points into a neat line. They make a lighter, more delicate break than a solid rule and suit minimal, clean layouts.",
      "Copy a row of dots and drop it on its own line, or use a short one as a bullet between words.",
    ],
    combos: [
      "••●••",
      "─┉┈◈◉◈┈┉",
      ".·:*¨༺༻¨*:·.",
      "•,¸,.·''·.,¸,•",
      "︑︒⚬∙︓·⠄",
      "»--•--«",
      ".⋅ ۵♡۵ ⋅.",
      "●・○・●・○・●",
      "●～●～●～●～●",
      "●❍°•°•°○°•°•°❍●",
      "×•-•-•-• ◆ •-•-•-•×",
      "°•°•°•°•°•°•°",
      "★・・・・・・★",
      "*+:｡.｡｡.｡:+*",
      "·•· ✦ ·•·",
      "⋆˚｡◦｡˚⋆◦⋆˚｡◦｡˚⋆",
      "∙∘○◦•◦○∘∙∘○◦•◦○∘∙",
      "·•⋅◦⋅•·◦·•⋅◦⋅•·",
      "◌•◍•◌•◍•◌•◍•◌",
    ],
  },
  {
    slug: "arrows",
    name: "Arrow Combos",
    hero: "»»——⍟——««",
    tagline: "Decorative arrow combos and pointers to lead the eye across text.",
    intro: [
      "Arrow combos turn simple pointers into decorated lines — fletched tails, repeated chevrons and curled call-outs. They're handy for directing attention to a link, a heading or the next line.",
      "Tap any arrow to copy it. Use one before a label, or run a long one across the page as a directional divider.",
    ],
    combos: [
      "»»——⍟——««",
      "➴➵➶➴➵➶➴➵➶",
      "◃───────────▹",
      "»»-----------►",
      "╰┈➤",
      "↶*ೃ✧˚. ❃ ↷ ˊ-",
      "﹀﹀︵↷",
      "❍⌇─➭",
      "»»ᅳᅳᅳᅳ►",
      "⇘⇙ ⇗⇖",
      "↳ ‧₊˚✧",
      "→→→ ⛤ ←←←",
      "·˚ ➶ ｡˚",
      "⋆˚࿔ ⟶⟶⟶ ࿔˚⋆",
      "·•➜·•➜·•➜·•➜·•",
      "↫↬↫ ✧ ↬↫↬",
      "⟿⋆⟿⋆⟿⋆⟿⋆⟿",
    ],
  },
  {
    slug: "flowers",
    name: "Flower Combos",
    hero: "❀ ✿ ❁ ✾ ❀",
    tagline: "Floral combos and blossom dividers for a fresh, garden-soft look.",
    intro: [
      "Flower combos string florettes and blossoms into little garlands and dividers. They bring a fresh, spring-like feel to a bio or caption and pair nicely with pastel themes.",
      "Copy a garland and place it around a name, or use a short sprig as a sweet sign-off.",
    ],
    combos: [
      "୨⎯ 🌹 ⎯୧",
      "ʚ🌹ɞ",
      "•┈••✦ 🌹 ✦••┈•",
      "◦◦,`°.✽✦✽.◦.✽✦✽.°`,◦◦",
      "❀ ✿ ❁ ✾ ❀",
      "⊰ ✿ ⊱",
      "✤ ❀ ✤ ❀ ✤",
      "✿ ˚ ❀ ˚ ✿",
      "⋆ ❀ ⋆ ❀ ⋆",
      "✎ (❁ᴗ͈ˬᴗ͈) ༉‧ ♡*.✧",
      "❀° ⋅ ❀ ⋅ °❀",
      "✿ ⋆ ❀ ⋆ ✿",
      "✽ ⋆｡°✩ ✽",
      "❁ ⋆ ❁ ⋆ ❁",
      "✾ ｡˚ ✿ ˚｡ ✾",
      "⊰ ❀ ─── ❀ ⊱",
    ],
  },
  {
    slug: "circles",
    name: "Circle Combos",
    hero: "●○●○●○●○●",
    tagline: "Circle, bead and orb combos for round, rhythmic dividers.",
    intro: [
      "Circle combos alternate filled and hollow dots, diamonds and orbs into a steady, rounded rhythm. They make a softer divider than a straight line and look great as a repeating motif across a page.",
      "Tap a combo to copy it. Use a short one as a bullet, or run a long row on its own line as a section break.",
    ],
    combos: [
      "●○●○●○●○●",
      "★○★○★○★",
      "◇──◆──◇──◆",
      "◈━◈━◈━◈━◈",
      "◈◆◈◆◈◆◈◆◈",
      "━◦○◦━◦○◦━",
      "✧○ꊞ○ꊞ○ꊞ○ꊞ○✧",
      "◇●○◆○●◇●○◆○●◇",
      "∘°∘♡∘°∘",
      "◦•●◉✿✿◉●•◦",
      "●∘◦❀◦∘●",
      "──⇌••⇋──",
      "≪ ◦ ❖ ◦ ≫",
      "──●◎●──",
      "━ ◦ ❖ ◦━",
      "○ ◯ ⊙ ◯ ○",
      "◍◌◍◌◍◌◍◌◍◌◍",
      "⊙⊚⊙⊚⊙⊚⊙⊚⊙⊚⊙",
      "◯●◦●◯●◦●◯●◦●◯",
    ],
  },
  {
    slug: "squares",
    name: "Square Combos",
    hero: "■□■□■□■□■",
    tagline: "Square, block and tile combos for crisp, geometric dividers.",
    intro: [
      "Square combos line up blocks, tiles and diamonds into a crisp, geometric strip. They give a bolder, more structured break than dots or waves and suit clean, modern layouts.",
      "Copy a row and drop it on its own line, or use a short block as a marker between sections.",
    ],
    combos: [
      "◈▢◈▢◈▢◈▢◈",
      "▣▣▣▣▣▣▣▣",
      "◆:*:◇:*:◆:*:◇:*:◆",
      "▰▰▰▰▰▰▰▰▰▰▰▰",
      "◇◆◇◆◇◆◇◆◇",
      "■□■□■□■□■□■□■",
      "◈◈◈▣▣◈◈◈▣▣◈◈◈",
      "▄▀▄▀▄▀▄▀▄▀▄▀▄",
      "▂ ▃ ▄ ▅ ▆ ▇ █ ▇ ▆ ▅ ▄ ▃ ▂",
      "▰▱▰▱▰▱▰▱▰▱▰▱",
      "┴┈┈■┈┈┴",
      "┬┈┈□┈┈┬",
      "▬▭▬ ✦ ▬▭▬",
      "◧ ◨ ◧ ◨ ◧",
      "❑ ❒ ❑ ❒ ❑",
      "▦▧▦▧▦▧▦",
      "▪▫▪▫▪▫▪▫▪▫▪",
      "◰◱◲◳◰◱◲◳◰◱◲◳",
      "▩▦▩▦▩▦▩▦▩▦▩",
    ],
  },
  {
    slug: "triangles",
    name: "Triangle Combos",
    hero: "◥✥◤ ◢✥◣",
    tagline: "Arrowhead and triangle combos for sharp, directional accents.",
    intro: [
      "Triangle combos build on arrowheads and chevrons for a sharp, pointed look. They work as bold dividers, banners around a heading, or directional accents that lead the eye.",
      "Tap one to copy it. Frame a title between two matching halves, or stack the two-line banners above and below your text.",
    ],
    combos: [
      "━━▲━━",
      "▔▔▿▔▔",
      "◤◢◣◥◤◢◣◥◤◢◣◥",
      "❢◥ ▬▬▬ ◆ ▬▬▬ ◤❢",
      "◥✥◤ ◢✥◣",
      "◄░░░░░░►",
      "° ∆ ------ ••• ----- ∆ °",
      "∆∇∆∇∆∇∆∇∆∇∆∇",
      "▲▼▲▼▲▼▲",
      "△▽△▽△▽△",
      "▷◁▷◁▷◁",
      "◤━━━━━◥\n◣━━━━━◢",
      "◬ ◭ ◮ ◭ ◬",
      "▴ ▵ ▴ ▵ ▴",
      "▲△▲△▲△▲△▲△▲",
      "◀▶◀▶◀▶◀▶◀▶◀",
      "⊳⊲⊳⊲⊳⊲⊳⊲⊳⊲⊳",
    ],
  },
  {
    slug: "music",
    name: "Music Symbols",
    hero: "♪♫•*¨*•♫♪",
    tagline: "Note, instrument and equalizer combos for a musical, now-playing mood.",
    intro: [
      "Music combos weave notes, instruments and little equalizer bars into musical flourishes. They're a quick way to give a bio or caption a song-and-rhythm feel without a real media player.",
      "Tap a combo to copy it, then drop it beside a lyric, a song title or a playlist link.",
    ],
    combos: [
      "♬ ♪ ♫ ♩",
      "♩ ♪ ♫ ♬ ♭ ♮ ♯",
      "🎵 ⋆｡°✩ 🎶",
      "♪♫•*¨*•♫♪",
      "🎧 ♫ ┈┈┈ ♫ 🎧",
      ".ılılıll 🎵 llılılı.",
      "♪ ┈ ♫ ┈ ♪ ┈ ♫",
      "🎶 ⋆ ♪ ⋆ 🎶",
      "🎸 🎻 🎹 🎺 🎷",
      "🎵 ━━━━ ♫ ━━━━ 🎵",
      "˚｡⋆ ♫ ⋆｡˚",
      "♪ ⋆ ｡ ♬ ｡ ⋆ ♪",
      "▁▂▃▄▅▆▇ ♪ ▇▆▅▄▃▂▁",
      "♡ ´¨`•.¸¸.♫ ♫.¸¸.•´¨` ♡",
      "⋆ ✦ ♪ ✦ ⋆",
      "♫ ♬ ♫ ♬ ♫",
    ],
  },
  {
    slug: "aesthetic",
    name: "Aesthetic Combos",
    hero: "⋆˚࿔ ˖ ✧˖°",
    tagline: "Tiny aesthetic symbol combos and flourishes to copy and paste.",
    intro: [
      "Aesthetic combos are short, self-contained clusters of stars, sparkles and ornaments — the little flourishes people scatter through bios, captions and usernames to set a mood.",
      "Each one copies as a single string. Pop them before or after your text, or stack a couple together for a softer, decorated look.",
    ],
    combos: [
      ": ̗̀➛",
      "⊹ ࣪ ˖",
      "౨ৎ˚₊",
      "𝜗𝜚⋆₊˚",
      "˙⋆✮",
      "ੈ✩‧₊˚",
      "⋆·˚ ༘ *",
      "ˏˋ°•*⁀➷",
      "·˚ ༘ ⋆.ೃ࿔*:･",
      "˚₊· ͟͟͞͞➳❥",
      "⋆.ೃ࿔*:･",
      "✧˖°.",
      "⋆˚࿔",
      "⋆⭒˚.⋆",
      "˚ ✦ ⋆｡°✩",
      "♡⸝⸝",
      "꒰ ♡ ꒱ ⋆",
      "✩₊˚.⋆☾⋆⁺₊✧",
      "⋆｡‧˚ʚ♡ɞ˚‧｡⋆",
      "₊˚⊹♡",
      "˗ˏˋ ★ ˎˊ˗",
      "˚｡⋆୨୧˚",
      "⊹ ࣪ ˖ ⟡",
      "✿ ˚. ᵎᵎ",
      "❀⋆.ೃ࿔*:･",
      "⋆˙⟡♡",
      "₊˚ʚ ᗢ ₊˚✧",
      "✦ ˖° ⋆",
      "꒰ ๑ ꒱",
      "⋆ ˚｡⋆୨♡୧⋆ ˚｡⋆",
      "⊹ ★ ⊹ ★ ⊹",
      "✩°｡⋆⸜ ⋆",
      "⋆.˚🦋༘⋆",
      "♡ ₊˚ ⟢ ˚₊ ♡",
      "⟢ ₊˚ ♡ ˚₊ ⟢",
      "⋆ ｡˚⟡˚｡ ⋆",
      "˚₊‧ ⋆｡☾｡⋆ ‧₊˚",
      "˖⁺‧₊˚ ♡ ˚₊‧⁺˖",
      "⋆｡˚ ☾ ˚｡⋆",
      "⊹ ࣪ ˖ ♡ ˖ ࣪ ⊹",
      "⟡ ⋆ ⟡ ⋆ ⟡",
      "✦ ⋆ ｡ ˚ ✦",
    ],
  },
  {
    slug: "ornate-frames",
    name: "Ornate Frames",
    hero: "╔═━─━─≪ ✦ ≫─━─━═╗\n╚═━─━─≪ ✦ ≫─━─━═╝",
    tagline: "Elaborate two-line ornamental frames to dress up a title or name.",
    intro: [
      "Ornate frames are the dressed-up cousins of a plain box border — layered corners, inlaid stars and flourished rules that turn a short line into a little engraved plaque.",
      "Each frame copies as two lines at once. Drop your words on the line between them, or paste a frame on its own as a heavier divider.",
    ],
    combos: [
      "╔═━─━─≪ ✦ ≫─━─━═╗\n╚═━─━─≪ ✦ ≫─━─━═╝",
      "☆.｡.:*・°★°・*:.｡.☆\n☆.｡.:*・°★°・*:.｡.☆",
      "┏━◈━━━━━━━━━◈━┓\n┗━◈━━━━━━━━━◈━┛",
      "✦•◦•─────────•◦•✦\n✦•◦•─────────•◦•✦",
      "❁ ✿ ❀ ───────── ❀ ✿ ❁\n❁ ✿ ❀ ───────── ❀ ✿ ❁",
      "╭─♡─────────♡─╮\n╰─♡─────────♡─╯",
      "❖═══════════════❖\n❖═══════════════❖",
      "⋆⭒˚｡⋆─────────⋆｡˚⭒⋆\n⋆⭒˚｡⋆─────────⋆｡˚⭒⋆",
      "♕ ･ﾟ✧ ───────── ✧ﾟ･ ♕\n♕ ･ﾟ✧ ───────── ✧ﾟ･ ♕",
      "༺❀༻────────────༺❀༻\n༺❀༻────────────༺❀༻",
      "⊰ ⋆ ⊱━━━━━━━━━⊰ ⋆ ⊱\n⊰ ⋆ ⊱━━━━━━━━━⊰ ⋆ ⊱",
      "◜◝ ✦ ◜◝ ✦ ◜◝ ✦ ◜◝\n◟◞ ✧ ◟◞ ✧ ◟◞ ✧ ◟◞",
      "❰═══◆═══════◆═══❱\n❰═══◆═══════◆═══❱",
      "✿｡‧˚────────˚‧｡✿\n✿｡‧˚────────˚‧｡✿",
      "┌─◌─◍─◌─◍─◌─┐\n└─◌─◍─◌─◍─◌─┘",
      "≪ °✦° ≫━━━━━━≪ °✦° ≫\n≪ °✦° ≫━━━━━━≪ °✦° ≫",
      "╓──♔──────────♔──╖\n╙──♔──────────♔──╜",
      "⊹⊱✿⊰⊹──────⊹⊱✿⊰⊹\n⊹⊱✿⊰⊹──────⊹⊱✿⊰⊹",
      "╔══✦══✿══✦══╗\n╚══✦══✿══✦══╝",
      "◦°˚✧──────────✧˚°◦\n◦°˚✧──────────✧˚°◦",
      "❃ ⊹ ❃ ─────────── ❃ ⊹ ❃\n❃ ⊹ ❃ ─────────── ❃ ⊹ ❃",
      "┎┈┈❀┈┈┈┈┈┈❀┈┈┒\n┖┈┈❀┈┈┈┈┈┈❀┈┈┚",
    ],
  },
  {
    slug: "celestial",
    name: "Celestial Flourishes",
    hero: "₊˚⊹♡⋆｡˚꒰ ⋆ ꒱˚｡⋆♡⊹˚₊",
    tagline: "Layered moon-and-star flourishes for a dreamy, celestial bio.",
    intro: [
      "Celestial flourishes layer crescent moons, tiny stars and soft sparkles into longer, symmetrical lines — a step up from a single sparkle when you want a whole dreamy banner.",
      "Each one copies as a single string. Wrap it around a name, float it at the top of a bio, or use it as a softer divider between sections.",
    ],
    combos: [
      "₊˚⊹♡⋆｡˚꒰ ⋆ ꒱˚｡⋆♡⊹˚₊",
      "⋆˚࿔ ⊹ ˚｡⋆ ✧ ⋆｡˚ ⊹ ࿔˚⋆",
      "˚ ༘ ⋆｡˚ ☾ ˚｡⋆ ༘ ˚",
      "⊹ ࣪ ˖ ⋆｡°✩ ✩°｡⋆ ˖ ࣪ ⊹",
      "˚₊‧꒰ ☆ ꒱‧₊˚",
      "✶⋆.˚ ⊹ ♡ ⊹ ˚.⋆✶",
      "❀°•°❀ ⊰ ✿ ⊱ ❀°•°❀",
      "·˚ ༘ ₊˚.⋆☾⋆⁺₊✧ ⋆.˚ ༘ ·",
      "⟡ ⋆｡˚ ✦ ˚｡⋆ ⟡",
      "˗ˏˋ ✦ ⋆ ✿ ⋆ ✦ ˎˊ˗",
      "｡ﾟ•┈୨♡୧┈•ﾟ｡",
      "✩₊˚.⋆☾⋆⁺₊ ✧ ₊⁺⋆☽⋆.˚₊✩",
      "⋆｡°✩₊˚ ⊹ ☾ ⊹ ˚₊✩°｡⋆",
      "˚ ⊹ ࣪ ✧ ⋆｡˚ ♡ ˚｡⋆ ✧ ࣪ ⊹ ˚",
      "⋅˚₊‧ ୨୧ ‧₊˚⋅ ✩ ⋅˚₊‧ ୨୧ ‧₊˚⋅",
      "｡ﾟ☆: *.☽.* :☆ﾟ｡",
      "·｡˚ ✧ ☾ ✦ ☽ ✧ ˚｡·",
      "⋆˙⟡ ⊹ ࣪ ˖ ⋆ ♡ ⋆ ˖ ࣪ ⊹ ⟡˙⋆",
      "✶ ₊˚ ⊹ ⋆ ✧ ⋆ ⊹ ˚₊ ✶",
      "˚ ✧ ｡⋆ ࿐ ⋆｡ ✧ ˚",
      "⊹₊ ⋆ ⌒⊹⌒ ⋆ ₊⊹",
      "·˚₊‧⁺˖ ⋆☾✦☽⋆ ˖⁺‧₊˚·",
    ],
  },
  {
    slug: "art-deco",
    name: "Art-Deco Frames",
    hero: "▰▱▰▱▰▱◆▱▰▱▰▱▰\n▰▱▰▱▰▱◆▱▰▱▰▱▰",
    tagline: "Bold geometric, art-deco style frames built from blocks and diamonds.",
    intro: [
      "Art-deco frames trade soft flourishes for hard geometry — stepped blocks, chevrons and faceted diamonds that give a heading a sharp, architectural border.",
      "Each frame copies as two lines. Set your words between them, or use a single line as a bold geometric divider.",
    ],
    combos: [
      "▰▱▰▱▰▱◆▱▰▱▰▱▰\n▰▱▰▱▰▱◆▱▰▱▰▱▰",
      "◣◢◣◢◣◢◣◢◣◢◣◢\n◥◤◥◤◥◤◥◤◥◤◥◤",
      "║═══════◈═══════║\n║═══════◈═══════║",
      "◇◆◇◆◇◆◇◆◇◆◇\n◇◆◇◆◇◆◇◆◇◆◇",
      "≫═══════◆═══════≪\n≫═══════◆═══════≪",
      "╳═╳═╳═◆═╳═╳═╳\n╳═╳═╳═◆═╳═╳═╳",
      "▲▽▲▽▲▽▲▽▲▽▲▽\n▽▲▽▲▽▲▽▲▽▲▽▲",
      "┃▰┃▰┃▰┃▰┃▰┃▰┃\n┃▰┃▰┃▰┃▰┃▰┃▰┃",
      "◢◤◢◤◢◤◆◥◣◥◣◥◣\n◢◤◢◤◢◤◆◥◣◥◣◥◣",
      "╬═══════◇═══════╬\n╬═══════◇═══════╬",
    ],
  },
  {
    slug: "botanical",
    name: "Vine & Botanical Borders",
    hero: "❀✾❀──────────❀✾❀\n❀✾❀──────────❀✾❀",
    tagline: "Leafy, flowering vine borders for a soft botanical look.",
    intro: [
      "Botanical borders run blossoms, buds and little leaves along a fine rule, like a pressed-flower frame around your words.",
      "Each copies as two lines to wrap a title, or one line as a flowering divider between sections.",
    ],
    combos: [
      "❀✾❀──────────❀✾❀\n❀✾❀──────────❀✾❀",
      "❦ ❧ ────────── ❧ ❦\n❦ ❧ ────────── ❧ ❦",
      "✿ ⊱ ❀ ⊰ ✿ ────── ✿ ⊱ ❀ ⊰ ✿\n✿ ⊱ ❀ ⊰ ✿ ────── ✿ ⊱ ❀ ⊰ ✿",
      "⊰❀⊱┄┄┄┄┄┄┄┄⊰❀⊱\n⊰❀⊱┄┄┄┄┄┄┄┄⊰❀⊱",
      "✽ ❀ ✾ ─────────── ✾ ❀ ✽\n✽ ❀ ✾ ─────────── ✾ ❀ ✽",
      "❁❃❋──────────❋❃❁\n❁❃❋──────────❋❃❁",
      "☘ ✿ ☘ ────────── ☘ ✿ ☘\n☘ ✿ ☘ ────────── ☘ ✿ ☘",
      "⟢❀⟣──────────⟢❀⟣\n⟢❀⟣──────────⟢❀⟣",
      "❀ ࿔ ✿ ────────── ✿ ࿔ ❀\n❀ ࿔ ✿ ────────── ✿ ࿔ ❀",
      "✿•❀•✾──────✾•❀•✿\n✿•❀•✾──────✾•❀•✿",
    ],
  },
  {
    slug: "kawaii",
    name: "Kawaii Frames",
    hero: "˚ʚ♡ɞ˚────────˚ʚ♡ɞ˚\n˚ʚ♡ɞ˚────────˚ʚ♡ɞ˚",
    tagline: "Cute, soft frames with little hearts and bows.",
    intro: [
      "Kawaii frames keep it sweet — tiny hearts, ribbon shapes and soft brackets that make a heading feel like a sticker.",
      "Each copies as two lines around your words, or one line as a cute divider.",
    ],
    combos: [
      "˚ʚ♡ɞ˚────────˚ʚ♡ɞ˚\n˚ʚ♡ɞ˚────────˚ʚ♡ɞ˚",
      "ʚ♡ɞ─────────ʚ♡ɞ\nʚ♡ɞ─────────ʚ♡ɞ",
      "꒰ ⑅ ꒱ ───────── ꒰ ⑅ ꒱\n꒰ ⑅ ꒱ ───────── ꒰ ⑅ ꒱",
      "♡｡ﾟ────────────ﾟ｡♡\n♡｡ﾟ────────────ﾟ｡♡",
      "⑅ ♡ ⑅ ───────── ⑅ ♡ ⑅\n⑅ ♡ ⑅ ───────── ⑅ ♡ ⑅",
      "✿ ♡ ✿ ─────────── ✿ ♡ ✿\n✿ ♡ ✿ ─────────── ✿ ♡ ✿",
      "♡ ⊹ ♡ ⊹ ♡──────♡ ⊹ ♡ ⊹ ♡\n♡ ⊹ ♡ ⊹ ♡──────♡ ⊹ ♡ ⊹ ♡",
      "˚ ✩ ♡ ✩ ˚ ──────── ˚ ✩ ♡ ✩ ˚\n˚ ✩ ♡ ✩ ˚ ──────── ˚ ✩ ♡ ✩ ˚",
      "ɞ˚୨୧˚ɞ──────ɞ˚୨୧˚ɞ\nɞ˚୨୧˚ɞ──────ɞ˚୨୧˚ɞ",
      "(♡)─────────(♡)\n(♡)─────────(♡)",
    ],
  },
  {
    slug: "gothic",
    name: "Gothic Frames",
    hero: "┣━━━━━━✠━━━━━━┫\n┣━━━━━━✠━━━━━━┫",
    tagline: "Heavy, dark frames with crosses and shaded fills.",
    intro: [
      "Gothic frames go bold and dramatic — heavy rules, shaded blocks and small crosses for a darker, ornate edge.",
      "Each copies as two lines to frame a heading, or one line as a heavy divider.",
    ],
    combos: [
      "┣━━━━━━✠━━━━━━┫\n┣━━━━━━✠━━━━━━┫",
      "▓▒░──────────░▒▓\n▓▒░──────────░▒▓",
      "╪═══════†═══════╪\n╪═══════†═══════╪",
      "☩ ━━━━━━━━━━━━ ☩\n☩ ━━━━━━━━━━━━ ☩",
      "╬══════♰══════╬\n╬══════♰══════╬",
      "┏━┅━┅━†━┅━┅━┓\n┗━┅━┅━†━┅━┅━┛",
      "⊹ ✠ ⊹ ━━━━━━━━ ⊹ ✠ ⊹\n⊹ ✠ ⊹ ━━━━━━━━ ⊹ ✠ ⊹",
      "▌▐▌▐▌▐◆▌▐▌▐▌▐\n▌▐▌▐▌▐◆▌▐▌▐▌▐",
      "╾━╾━╾━◆━╼━╼━╼\n╾━╾━╾━◆━╼━╼━╼",
      "▚▚▚▚▚▚◆▚▚▚▚▚▚\n▞▞▞▞▞▞◆▞▞▞▞▞▞",
    ],
  },
  {
    slug: "banners",
    name: "Banners & Ribbons",
    hero: "⊰•◦❥◦•⊱",
    tagline: "Ribbon and banner flourishes that wrap a single line.",
    intro: [
      "Banners are one-line ribbons — pointed ends, a centred emblem and a fine rule — made to sit on either side of a short heading.",
      "Each copies as a single string; drop a heading in the middle, or use it on its own as a flagged divider.",
    ],
    combos: [
      "⊰•◦❥◦•⊱",
      "❰━━━━━ ✦ ━━━━━❱",
      "⊱ ─────── ❈ ─────── ⊰",
      "◤━━━━━ ✿ ━━━━━◢",
      "≫───────♛───────≪",
      "❮━━━━━ ❖ ━━━━━❯",
      "⊰ ⋅•⋅ ⊰ ✦ ⊱ ⋅•⋅ ⊱",
      "⊱✦⊰━━━━━━━━⊱✦⊰",
      "►─────── ◆ ───────◄",
      "◥◤ ─────── ✧ ─────── ◢◣",
    ],
  },
  {
    slug: "corners",
    name: "Corner Frames",
    hero: "┏━━━━━ ✦ ━━━━━┓\n┗━━━━━ ✦ ━━━━━┛",
    tagline: "Frames showing off a different decorative corner on each style.",
    intro: [
      "Corner frames each lead with a different corner treatment — square joints, rounded turns, bracket tips, shadowed blocks — so you can pick the corner that fits the mood.",
      "Each copies as two lines around your words, or one line as a tidy divider.",
    ],
    combos: [
      "┏━━━━━ ✦ ━━━━━┓\n┗━━━━━ ✦ ━━━━━┛",
      "╭━━━━━ ✿ ━━━━━╮\n╰━━━━━ ✿ ━━━━━╯",
      "⌜━━━━━ ♡ ━━━━━⌝\n⌞━━━━━ ♡ ━━━━━⌟",
      "┎━━━━━ ❖ ━━━━━┒\n┖━━━━━ ❖ ━━━━━┚",
      "▗▄▄▄▄ ★ ▄▄▄▄▖\n▝▀▀▀▀ ★ ▀▀▀▀▘",
      "╒═════ ◆ ═════╕\n╘═════ ◆ ═════╛",
      "┍━━━━━ ✧ ━━━━━┑\n┕━━━━━ ✧ ━━━━━┙",
      "╓───── ❀ ─────╖\n╙───── ❀ ─────╜",
      "「───── ⊹ ─────」\n「───── ⊹ ─────」",
      "╔───── ☆ ─────╗\n╚───── ☆ ─────╝",
    ],
  },
  {
    slug: "art-boxes",
    name: "Ornamental Boxes",
    hero: "╔═══════════════╗\n║   ✦   ✿   ✦   ║\n╚═══════════════╝",
    tagline: "Three-line ornamental boxes you copy whole as a finished plaque.",
    intro: [
      "Ornamental boxes are complete little plaques — a top edge, a decorated middle row and a matching bottom edge — meant to be copied as one finished piece.",
      "They look their best in a fixed-width place like a code block, terminal or any monospaced bio, where the rows line up exactly.",
    ],
    combos: [
      "╔═══════════════╗\n║   ✦   ✿   ✦   ║\n╚═══════════════╝",
      "╭───────────────╮\n│ ⋆ ˚｡⋆ ୨୧ ⋆ ˚｡⋆ │\n╰───────────────╯",
      "┏━━━━━━━━━━━━━┓\n┃ ♡ · · · · · · · ♡ ┃\n┗━━━━━━━━━━━━━┛",
      "✿❀✿❀✿❀✿❀✿❀✿\n❀ · · · · · · · · · ❀\n✿❀✿❀✿❀✿❀✿❀✿",
      "◤━━━━━━━━━━━◥\n┃   ✦  ❖  ✦   ┃\n◣━━━━━━━━━━━◢",
      "╔♛═══════════♛╗\n║ ⋆ ✧ ⋆ ✧ ⋆ ✧ ⋆ ║\n╚♛═══════════♛╝",
      "▛▀▀▀▀▀▀▀▀▀▀▀▜\n▌ ❀ ⊹ ❀ ⊹ ❀ ⊹ ▐\n▙▄▄▄▄▄▄▄▄▄▄▄▟",
      "╭┈┈┈┈┈┈┈┈┈┈┈╮\n┊ ✩ · ♡ · ✩ · ♡ ┊\n╰┈┈┈┈┈┈┈┈┈┈┈╯",
      "┌───────────────┐\n│ ☾ ⋆ ✦ ⋆ ✦ ⋆ ☽ │\n└───────────────┘",
      "═══════◆═══════\n   ✦ · ✿ · ✦\n═══════◆═══════",
    ],
  },
];

export function getDecorativeCollection(slug: string): DecorativeCollection | undefined {
  return decorativeCollections.find((c) => c.slug === slug);
}
